/**
 * Course outline assembly.
 *
 * A course is Modules → Lessons, but modules were added after lessons existed,
 * so every view has to cope with three cases at once: lessons filed under a
 * module, lessons with no module, and lessons pointing at a module that has
 * since been deleted. All three are folded into one ordered list of groups so
 * callers never have to special-case them.
 */

const UNGROUPED_TITLE = 'Course Content';

function byOrderThen(key) {
  return (a, b) => {
    const delta = (a.order ?? 0) - (b.order ?? 0);
    if (delta !== 0) return delta;
    return String(a[key] || '').localeCompare(String(b[key] || ''));
  };
}

/**
 * Group lessons under their modules.
 *
 * Returns `[{ id, title, summary, showHeading, lessons }]` in display order.
 * Modules come first in their configured order; anything ungrouped or orphaned
 * lands in a trailing bucket. `showHeading` is false when the whole course is
 * one flat list, so a course with no modules doesn't grow a pointless header.
 */
export function groupLessonsByModule(lessons, modules) {
  const lessonList = [...(lessons || [])].sort(byOrderThen('title'));
  const moduleList = [...(modules || [])].sort(byOrderThen('title'));

  const byModule = new Map(moduleList.map((m) => [m.id, []]));
  const ungrouped = [];

  lessonList.forEach((lesson) => {
    const moduleId = lesson.module_id || '';
    // A lesson whose module was deleted still has the stale id; treat it as
    // ungrouped rather than dropping it from the outline entirely.
    if (moduleId && byModule.has(moduleId)) {
      byModule.get(moduleId).push(lesson);
    } else {
      ungrouped.push(lesson);
    }
  });

  const groups = moduleList
    .map((module) => ({
      id: module.id,
      title: module.title,
      summary: module.summary || '',
      showHeading: true,
      lessons: byModule.get(module.id) || [],
    }))
    .filter((group) => group.lessons.length > 0);

  if (ungrouped.length) {
    groups.push({
      id: '',
      title: UNGROUPED_TITLE,
      summary: '',
      // Only label the bucket when it sits alongside real modules.
      showHeading: groups.length > 0,
      lessons: ungrouped,
    });
  }

  return groups;
}

/**
 * Same grouping, but every module is kept even when empty and the ungrouped
 * bucket always appears. The instructor builder needs stable drop targets —
 * an empty module you just created must not vanish before you can fill it.
 */
export function buildEditableOutline(lessons, modules) {
  const lessonList = [...(lessons || [])].sort(byOrderThen('title'));
  const moduleList = [...(modules || [])].sort(byOrderThen('title'));

  const byModule = new Map(moduleList.map((m) => [m.id, []]));
  const ungrouped = [];

  lessonList.forEach((lesson) => {
    const moduleId = lesson.module_id || '';
    if (moduleId && byModule.has(moduleId)) byModule.get(moduleId).push(lesson);
    else ungrouped.push(lesson);
  });

  return [
    ...moduleList.map((module) => ({
      id: module.id,
      title: module.title,
      summary: module.summary || '',
      order: module.order ?? 0,
      isModule: true,
      lessons: byModule.get(module.id) || [],
    })),
    {
      id: '',
      title: UNGROUPED_TITLE,
      summary: '',
      order: Number.MAX_SAFE_INTEGER,
      isModule: false,
      lessons: ungrouped,
    },
  ];
}

/**
 * Flatten an outline back into the payload the reorder endpoint expects.
 * `order` is renumbered from zero within each module so positions stay dense
 * after inserts and deletes.
 */
export function toReorderPayload(groups) {
  const items = [];
  (groups || []).forEach((group) => {
    group.lessons.forEach((lesson, index) => {
      items.push({ lesson_id: lesson.id, module_id: group.id || '', order: index });
    });
  });
  return items;
}

/** Count of lessons a student can actually see, for instructor-side summaries. */
export function publishedLessonCount(lessons) {
  return (lessons || []).filter((lesson) => lesson.status !== 'draft').length;
}
